/**
 * One shape for "a food you can log", whatever it came from: USDA, Open Food
 * Facts, a label read, your own custom food, a recipe, or something you logged
 * before. The portion picker only ever sees this.
 */

import type { Nutrients } from './nutrients.ts'
import type { ServingDef } from './servings.ts'

export type FoodSource = 'usda' | 'cnf' | 'restaurant' | 'off' | 'custom' | 'recipe' | 'label' | 'ai_photo' | 'ai_text' | 'meal' | 'quick'

export type FoodItem = {
  ref: string | null
  source: FoodSource
  name: string
  brand?: string | null
  basis: 'g' | 'ml'
  per100: Nutrients
  servings: ServingDef[]
  barcode?: string | null
  verified?: boolean
  /** Restaurant item whose weight the chain doesn't publish: per100 is against
   * a nominal weight, so only its serving makes sense — never grams. */
  nominal?: boolean
  /** Restaurant data from the chain's US guide (no Canadian one exists). */
  country?: string | null
  /** What the person picked last time, to preselect. */
  lastGrams?: number | null
  lastServing?: string | null
  lastQty?: number | null
}

/** Grams of the nominal serving the food index uses for restaurant items
 * without a published weight (tools/build_food_index.py NOMINAL_G). */
export const NOMINAL_G = 500

/** The logged row's source + source_ref for a FoodItem. */
export function refParts(item: FoodItem): { source: FoodSource; source_ref: string | null } {
  if (!item.ref) return { source: item.source, source_ref: null }
  const i = item.ref.indexOf(':')
  return { source: item.source, source_ref: i >= 0 ? item.ref.slice(i + 1) : item.ref }
}

export const DEFAULT_MEALS = ['Breakfast', 'Lunch', 'Dinner', 'Snacks']

export function mealNames(custom?: string[] | null): string[] {
  return custom && custom.length ? custom : DEFAULT_MEALS
}

/** Meal slot for "now": breakfast before 10:30, lunch before 15:00, dinner before 21:00. */
export function mealForNow(d = new Date()): number {
  const h = d.getHours() + d.getMinutes() / 60
  return h < 10.5 ? 0 : h < 15 ? 1 : h < 21 ? 2 : 3
}

export function sourceLabel(s: string): string {
  return ({
    usda: 'USDA', cnf: 'Health Canada', restaurant: 'Restaurant', off: 'Open Food Facts', custom: 'My food', recipe: 'Recipe', label: 'Label',
    ai_photo: 'Photo estimate', ai_text: 'Estimate', meal: 'Saved meal', quick: 'Quick add',
  } as Record<string, string>)[s] ?? s
}
